import dayjs from 'dayjs';
import { P } from '../theme/tokens';

export const STAGES = [
  { key: 'Lead' },
  { key: 'Prequalification Complete' },
  { key: 'Tour Scheduled' },
  { key: 'Application Submitted' },
  { key: 'Background Check Submitted' },
  { key: 'Fully Qualified', color: P.success, pill: { label: 'Ready for contract', color: P.success }, verb: 'Qualified' },
  {
    key: 'Not Eligible',
    color: P.danger,
    pill: { label: 'Recycle eligible', color: P.subtle },
    verb: 'Closed',
    note: '↺ Not a dead end — may re-enter the pipeline as a new lead later (e.g. after 6 months, per policy).'
  },
  { key: 'Contract Signed', color: P.info, pill: { label: 'Move-in scheduled', color: P.info }, verb: 'Signed' }
];

const STAGE_VERBS = { 'Application Submitted': 'Submitted', 'Background Check Submitted': 'Submitted' };

export const SCORE_MODEL = [
  {
    group: 'Engagement',
    color: P.info,
    cap: 3,
    factors: [
      { key: 'inbound', label: 'Inbound self-generated lead', weight: 0.5, how: 'They came to us — website, Zillow, or referral rather than an outbound list.' },
      { key: 'responsive', label: 'Responsive to texts', weight: 1, how: 'Replied to at least one outbound message.' },
      { key: 'droveBy', label: 'Drove by property', weight: 1.5, how: 'Confirmed on a call that they went and looked at it.' }
    ]
  },
  {
    group: 'Qualification',
    color: P.purple,
    cap: 5,
    factors: [
      { key: 'prequal', label: 'Prequalification complete', weight: 2.5, how: 'All 13 drill items captured — income, employment, rental history.' },
      { key: 'petOk', label: 'Pet on file (compatible unit)', weight: 0.5, how: 'Pet disclosed and it fits the unit’s policy.' },
      { key: 'noLiens', label: 'No liens on file', weight: 2, how: 'No liens or collections surfaced in screening.' }
    ]
  },
  {
    group: 'Screening',
    color: P.warning,
    cap: 3,
    factors: [
      { key: 'bgSubmitted', label: 'Background check submitted', weight: 1, how: 'Consent given and the check is running.' },
      { key: 'bgClear', label: 'Background check clear', weight: 2, how: 'Returned with nothing disqualifying.' },
      { key: 'bgFlag', label: 'Background check flagged (eviction record)', weight: -4, how: 'An eviction on record — the one heavily negative signal.' }
    ]
  },
  {
    group: 'Commitment',
    color: P.success,
    cap: 4.5,
    factors: [
      { key: 'tourScheduled', label: 'Tour scheduled', weight: 1, how: 'A tour is on the leasing calendar.' },
      { key: 'tourCompleted', label: 'Tour completed', weight: 1, how: 'They showed up and walked the unit.' },
      { key: 'appSubmitted', label: 'Application submitted', weight: 1.5, how: 'Application in, fee paid.' },
      { key: 'contract', label: 'Contract executed', weight: 1, how: 'Lease signed.' }
    ]
  }
];

export const SCORE_BANDS = [
  { label: 'Priority', color: P.success, min: 7, note: '7.0–10 · call first' },
  { label: 'Working', color: P.warning, min: 4, note: '4.0–6.9 · needs a next step' },
  { label: 'Early', color: P.danger, min: 0, note: 'below 4.0 · unqualified so far' }
];

export const SCORE_FOOTNOTE =
  'The score ranks who to call first. It is not an approval. A leasing decision is always made by a person, and any single flagged item routes to a manager regardless of score.';

export const earnedSignals = (p) =>
  SCORE_MODEL.flatMap((g) => g.factors.filter((f) => p.signals?.[f.key]).map((f) => ({ ...f, group: g.group, color: g.color })));

export const rawScore = (p) => earnedSignals(p).reduce((sum, f) => sum + f.weight, 0);

export const scoreOf = (p) => Math.max(0, Math.min(10, rawScore(p)));

export const bandOf = (score) => SCORE_BANDS.find((b) => score >= b.min);

export const strongestSignal = (p) =>
  earnedSignals(p)
    .filter((f) => f.weight > 0)
    .reduce((best, f) => (!best || f.weight > best.weight ? f : best), null);

export const signed = (n) => `${n < 0 ? '−' : '+'}${Math.abs(n).toFixed(1)}`;

export const ago = (at) => {
  const mins = dayjs().diff(at, 'minute');
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const tourLabel = (p) => p.tour?.start && dayjs(p.tour.start).format('ddd h:mm A');

export const stageFull = (p) => (p.pipelineStage === 'Tour Scheduled' && tourLabel(p) ? `Tour Scheduled — ${tourLabel(p)}` : p.pipelineStage);

export const whenOf = (p) => {
  if (p.pipelineStage === 'Tour Scheduled' && tourLabel(p)) return `Tour ${tourLabel(p)}`;
  const at = ago(p.lastContactAt || p.createdAt);
  const verb = STAGE_VERBS[p.pipelineStage] || STAGES.find((s) => s.key === p.pipelineStage)?.verb;
  return verb ? `${verb} ${at}` : at;
};

export const agentOf = (p) => p.assignedTo || 'Unassigned';

export const isSpanish = (p) => (p.language || p.intake?.language) === 'Spanish';

export const cardPills = (p) => {
  const agent = agentOf(p);
  return [
    { label: agent, color: agent === 'Unassigned' ? P.warning : P.cyan },
    isSpanish(p) && { label: 'ES', color: P.purple },
    p.flagged && { label: 'Transfer failed', color: P.danger }
  ].filter(Boolean);
};

export const TERMINAL_STAGES = ['Fully Qualified', 'Contract Signed', 'Not Eligible'];

/* ---------- Leasing board (Pipelines › Leasing) ---------- */
// The design shows 8 linear columns. The backend pipelineStage keys don't line up 1:1,
// so we map each key onto a column, and a column back onto the canonical key to persist a drag.
export const BOARD_STAGES = [
  { col: 'prospect', label: 'Prospect' },
  { col: 'prequalified', label: 'Prequalified' },
  { col: 'tour', label: 'Tour scheduled' },
  { col: 'app-sent', label: 'Application sent' },
  { col: 'app-complete', label: 'Application complete' },
  { col: 'lease-signed', label: 'Lease signed' },
  { col: 'move-in', label: 'Move-in scheduled' },
  { col: 'won', label: 'Won - moved in' }
];

const STAGE_TO_COLUMN = {
  Lead: 'prospect',
  'Prequalification Complete': 'prequalified',
  'Tour Scheduled': 'tour',
  'Application Submitted': 'app-sent',
  'Background Check Submitted': 'app-complete',
  'Fully Qualified': 'app-complete',
  'Contract Signed': 'lease-signed',
  'Move-in Scheduled': 'move-in'
  // 'Not Eligible' → no column (hidden from the board); converted prospects leave PROSPECT_SCOPE.
};

const COLUMN_TO_STAGE = {
  prospect: 'Lead',
  prequalified: 'Prequalification Complete',
  tour: 'Tour Scheduled',
  'app-sent': 'Application Submitted',
  'app-complete': 'Background Check Submitted',
  'lease-signed': 'Contract Signed',
  'move-in': 'Move-in Scheduled'
  // 'won' is terminal (set by convert), so it isn't a drop target.
};

export const columnOf = (p) => STAGE_TO_COLUMN[p.pipelineStage] || null;
// Canonical backend stage to persist when a card is dropped on a column (null = not a drop target).
export const stageForColumn = (col) => COLUMN_TO_STAGE[col] || null;
export const labelOf = (stage) => BOARD_STAGES.find((s) => s.col === STAGE_TO_COLUMN[stage])?.label || stage;

export const daysInStage = (p) => {
  const at = p.stageAt || p.lastContactAt || p.createdAt;
  return at ? Math.max(0, dayjs().diff(at, 'day')) : null;
};
